import { EsPCAReportField, PCAReportField } from 'c-sdk';
import type { TranslationPath } from 'c-translation';
import { MapDataType } from 'c-main/Components/Campaign/CampaignReports/types';

export type SummaryPresetKey = 'default' | 'test';
export type SummaryWidgetMetric = string;
export type SummaryWidgetSource = 'summary' | 'groupByField' | 'frameIdMapData' | 'custom';
export type SummaryWidgetKind = 'line' | 'bar' | 'map';
export type SummaryWidgetGroupBy = string;
export type SummaryMapDisplayMode = 'heatmap' | 'screens' | 'both';

export type SummaryWidgetDisplayMode = 'chart' | 'leaderboard' | 'overlayBars';

export type SummaryWidgetConfig = {
    id: string;
    metric: SummaryWidgetMetric;
    source: SummaryWidgetSource;
    kind: SummaryWidgetKind;
    groupBy: SummaryWidgetGroupBy[];
    mapDataType?: MapDataType;
    mapDisplayMode?: SummaryMapDisplayMode;
    title?: string;
    isPrimary?: boolean;
    displayMode?: SummaryWidgetDisplayMode;
    expandedDisplayMode?: SummaryWidgetDisplayMode;
    barOrientation?: 'vertical' | 'horizontal';
    expandedBarOrientation?: 'vertical' | 'horizontal';
    cumulative?: boolean;
    defaultShowNumbers?: boolean;
    defaultShowPercent?: boolean;
    defaultViewFilter?: 'Expected' | 'Delivered' | 'Both';
    defaultSort?: 'asc' | 'desc' | 'alpha' | 'none';
    smallItemLimit?: number;
};

export type SummaryLayoutItem = { widgetId: string; widthPct: number };
// heightPx: optional per-row height for the widgets in that row. Undefined => default height.
export type SummaryLayoutRow = { items: SummaryLayoutItem[]; heightPx?: number };
export type SummaryLayoutConfig = {
    mode: 'carousel' | 'grid';
    rows: SummaryLayoutRow[];
    scroll?: boolean;
};

// Row-height bounds (px) for the Layout tab height control.
export const MIN_ROW_HEIGHT_PX = 160;
export const MAX_ROW_HEIGHT_PX = 560;
export const DEFAULT_ROW_HEIGHT_PX = 240;

export const clampRowHeight = (value: number | undefined): number | undefined => {
    if (value == null || !Number.isFinite(value)) return undefined;
    return Math.max(MIN_ROW_HEIGHT_PX, Math.min(MAX_ROW_HEIGHT_PX, Math.round(value)));
};

// Default layout stacks each widget on its own full-width row (grid), rather than the
// primary-plus-carousel arrangement.
export const getDefaultSummaryLayout = (): SummaryLayoutConfig => ({
    mode: 'grid',
    rows: [],
});

export const MIN_ITEM_WIDTH_PCT = 25;
const MAX_ITEMS_PER_FULL_ROW = Math.floor(100 / MIN_ITEM_WIDTH_PCT);

const evenWidths = (count: number): number[] => {
    if (count <= 0) return [];
    if (count > MAX_ITEMS_PER_FULL_ROW) return new Array(count).fill(MIN_ITEM_WIDTH_PCT);
    const base = Math.floor(100 / count);
    const widths = new Array(count).fill(base);
    widths[count - 1] = 100 - base * (count - 1);
    return widths;
};

const normaliseRowWidths = (items: SummaryLayoutItem[]): SummaryLayoutItem[] => {
    if (items.length === 0) return items;
    const clamped = items.map(it => ({
        ...it,
        widthPct: Math.max(MIN_ITEM_WIDTH_PCT, Number.isFinite(it.widthPct) ? it.widthPct : 0),
    }));
    if (clamped.length > MAX_ITEMS_PER_FULL_ROW) return clamped;
    const total = clamped.reduce((acc, it) => acc + it.widthPct, 0);
    if (total <= 0) {
        const evens = evenWidths(clamped.length);
        return clamped.map((it, i) => ({ ...it, widthPct: evens[i] }));
    }
    const scaled = clamped.map(it => ({
        ...it,
        widthPct: Math.round((it.widthPct / total) * 100),
    }));
    const scaledTotal = scaled.reduce((acc, it) => acc + it.widthPct, 0);
    scaled[scaled.length - 1].widthPct += 100 - scaledTotal;
    return scaled;
};

export const normalizeSummaryLayout = (
    layout: SummaryLayoutConfig | undefined,
    widgets: SummaryWidgetConfig[],
): SummaryLayoutConfig => {
    const base = layout ?? getDefaultSummaryLayout();
    const validIds = new Set(widgets.map(w => w.id));
    const seen = new Set<string>();

    const rows = (base.rows ?? [])
        .map(row => ({
            heightPx: clampRowHeight(row.heightPx),
            items: (row.items ?? []).filter(it => {
                if (!validIds.has(it.widgetId) || seen.has(it.widgetId)) return false;
                seen.add(it.widgetId);
                return true;
            }),
        }))
        .map(row => ({ heightPx: row.heightPx, items: normaliseRowWidths(row.items) }));

    // Each not-yet-placed widget gets its own full-width row, so the default view stacks
    // one widget per line.
    const unplaced = widgets.filter(w => !seen.has(w.id)).map(w => w.id);
    unplaced.forEach(widgetId =>
        rows.push({ heightPx: undefined, items: [{ widgetId, widthPct: 100 }] }),
    );

    return { mode: base.mode === 'grid' ? 'grid' : 'carousel', rows, scroll: base.scroll === true };
};

export const evenRowWidths = evenWidths;

/** Per-box customisation for the top summary tiles (PCATotalsByMetric). */
export type TopBoxConfig = {
    showGraph: boolean;
    showAllData: boolean;
    cumulative: boolean;
    // When true the box is not rendered at all (lets a user drop e.g. the Plays box).
    hidden?: boolean;
};

// Default view is data-first: no graph, all data shown.
export const DEFAULT_TOP_BOX_CONFIG: TopBoxConfig = {
    showGraph: false,
    showAllData: true,
    cumulative: true,
    hidden: false,
};

export type TopBoxDef = { key: string; label: string };

/** Customisable top boxes per campaign type. Boxes with no chart/details (e.g. value) are omitted. */
export const TOP_BOX_DEFS: { standard: TopBoxDef[]; dsp: TopBoxDef[] } = {
    standard: [
        { key: 'impacts', label: 'Impacts' },
        { key: 'plays', label: 'Plays' },
    ],
    dsp: [
        { key: 'overall', label: 'Overall delivery' },
        { key: 'toDate', label: 'Delivery to date' },
        { key: 'analysis', label: 'Impression analysis' },
    ],
};

export const getTopBoxDefs = (isDsp: boolean): TopBoxDef[] =>
    isDsp ? TOP_BOX_DEFS.dsp : TOP_BOX_DEFS.standard;

export const getTopBoxConfig = (
    map: Record<string, TopBoxConfig> | undefined,
    key: string,
): TopBoxConfig => map?.[key] ?? DEFAULT_TOP_BOX_CONFIG;

export const SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS: Partial<
    Record<SummaryWidgetGroupBy, TranslationPath>
> = {
    date: 'Modules.Main.Campaigns.Overview.Reports.fields.day',
    display_owner: 'Modules.Main.Campaigns.Overview.Reports.fields.display_owner',
    display_env: 'Modules.Main.Campaigns.Overview.Reports.fields.display_env',
    display_format: 'Modules.Main.Campaigns.Overview.Reports.fields.display_format',
    display_owner_format: 'Modules.Main.Campaigns.Overview.Reports.fields.display_owner_format',
    display_tv_region: 'Modules.Main.Campaigns.Overview.Reports.fields.display_tv_region',
    day_of_week: 'Modules.Main.Campaigns.Overview.Reports.fields.dow',
    hour: 'Modules.Main.Campaigns.Overview.Reports.fields.hour',
    map: 'Modules.Main.Campaigns.Overview.Analytics.summaryTitleHeatmap',
};

export const SUMMARY_WIDGET_GROUP_BY_ES_FIELD_MAP: Partial<
    Record<SummaryWidgetGroupBy, EsPCAReportField>
> = {
    display_owner: EsPCAReportField.DisplayOwner,
    display_env: EsPCAReportField.DisplayEnv,
    display_format: EsPCAReportField.DisplayFormat,
    display_tv_region: EsPCAReportField.DisplayTvRegion,
    display_conurbation: EsPCAReportField.DisplayConurbation,
    creative_name: EsPCAReportField.CreativeName,
    day_of_week: EsPCAReportField.Dow,
    hour: EsPCAReportField.Hour,
};

export const SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP: Partial<
    Record<SummaryWidgetGroupBy, PCAReportField>
> = {
    display_owner: PCAReportField.MediaOwner,
    display_tv_region: PCAReportField.TvRegion,
    display_conurbation: PCAReportField.Conurbation,
    creative_name: PCAReportField.CreativeName,
    day_of_week: PCAReportField.DayOfWeek,
    hour: PCAReportField.Hour,
};

export const SUMMARY_WIDGET_METRIC_OPTIONS = [
    { value: 'impacts', labelPath: 'Modules.Main.Campaigns.Overview.Analytics.impactsTitle' },
    { value: 'plays', labelPath: 'Modules.Main.Campaigns.Overview.Analytics.playsTitle' },
] as const;

export const SUMMARY_WIDGET_KIND_OPTIONS = [
    {
        value: 'line',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.line',
    },
    {
        value: 'bar',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.bar',
    },
    {
        value: 'map',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.chartTypes.map',
    },
] as const;

export const SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS = [
    {
        value: MapDataType.HeatActual,
        labelPath: 'actual',
    },
    {
        value: MapDataType.HeatExpected,
        labelPath: 'expected',
    },
    {
        value: MapDataType.HeatPerformance,
        labelPath: 'performance',
    },
] as const;

export const SUMMARY_WIDGET_MAP_DISPLAY_OPTIONS = [
    {
        value: 'heatmap',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.heatmapOnly',
    },
    { value: 'screens', labelPath: 'Modules.Main.Campaigns.Overview.Reports.map.markersToggle' },
    {
        value: 'both',
        labelPath: 'Modules.Main.Campaigns.Overview.SummaryCustomization.heatmapAndScreens',
    },
] as const;

export const SUMMARY_WIDGET_GROUP_BY_OPTIONS = [
    'date',
    'display_owner',
    'display_env',
    'display_format',
    'display_tv_region',
    'display_conurbation',
    'creative_name',
    'day_of_week',
    'hour',
] as const;

export const SUMMARY_WIDGET_PRESETS: Record<
    SummaryPresetKey,
    { standard: SummaryWidgetConfig[]; dsp: SummaryWidgetConfig[] }
> = {
    default: {
        standard: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'running_total_plays',
                metric: 'plays',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_region',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_tv_region'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_day_of_week',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['day_of_week'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_hour',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['hour'],
                displayMode: 'overlayBars',
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
        dsp: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_region',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_tv_region'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_day_of_week',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['day_of_week'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_hour',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['hour'],
                displayMode: 'overlayBars',
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
    },
    test: {
        standard: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_environment',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env'],
            },
            {
                id: 'impacts_by_media_owner_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_owner', 'display_format'],
            },
            {
                id: 'impacts_by_env_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env', 'display_format'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
        dsp: [
            {
                id: 'running_total_impacts',
                metric: 'impacts',
                source: 'summary',
                kind: 'line',
                groupBy: ['date'],
            },
            {
                id: 'impacts_by_media_owner',
                metric: 'impacts',
                source: 'summary',
                kind: 'bar',
                groupBy: ['display_owner'],
                displayMode: 'leaderboard',
            },
            {
                id: 'impacts_by_environment',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_env'],
            },
            {
                id: 'impacts_by_media_owner_format',
                metric: 'impacts',
                source: 'groupByField',
                kind: 'bar',
                groupBy: ['display_owner', 'display_format'],
            },
            {
                id: 'impacts_heatmap',
                metric: 'impacts',
                source: 'summary',
                kind: 'map',
                groupBy: [],
                mapDataType: MapDataType.HeatActual,
                mapDisplayMode: 'heatmap',
            },
        ],
    },
};

const SUMMARY_BAR_FIELDS_AVAILABLE_IN_SUMMARY = new Set([
    'date',
    'display_owner',
    'display_tv_region',
    'day_of_week',
]);

export const inferSummaryWidgetSource = (
    widget: Pick<SummaryWidgetConfig, 'kind' | 'metric' | 'groupBy'>,
): SummaryWidgetSource => {
    const [primaryGroupBy] = widget.groupBy;

    if (widget.kind === 'map') {
        return 'summary';
    }

    if (widget.kind === 'line') {
        return primaryGroupBy === 'date' ? 'summary' : 'groupByField';
    }

    if (
        widget.kind === 'bar' &&
        widget.groupBy.length === 1 &&
        SUMMARY_BAR_FIELDS_AVAILABLE_IN_SUMMARY.has(primaryGroupBy)
    ) {
        return 'summary';
    }

    return 'groupByField';
};

export const inferSummaryWidgetDisplayMode = (
    widget: Pick<SummaryWidgetConfig, 'kind' | 'groupBy' | 'displayMode'>,
): SummaryWidgetDisplayMode | undefined => {
    if (widget.kind !== 'bar') return undefined;
    if (widget.groupBy.length === 0) return undefined;
    const [field] = widget.groupBy;
    if (widget.groupBy.length === 1 && field === 'date') return widget.displayMode;
    if (widget.groupBy.length === 1 && field === 'hour') return 'overlayBars';
    return widget.displayMode ?? 'leaderboard';
};

export const normalizeSummaryWidgetConfig = (widget: SummaryWidgetConfig): SummaryWidgetConfig => {
    const groupBy = widget.kind === 'map' ? [] : widget.groupBy.filter(Boolean);
    const next: SummaryWidgetConfig = {
        ...widget,
        groupBy,
        mapDataType:
            widget.kind === 'map'
                ? widget.mapDataType === MapDataType.Marker ||
                  widget.mapDataType === MapDataType.HeatInSchedule ||
                  widget.mapDataType === MapDataType.HeatOutSchedule ||
                  widget.mapDataType === MapDataType.HeatUnbooked
                    ? MapDataType.HeatActual
                    : widget.mapDataType ?? MapDataType.HeatActual
                : undefined,
        mapDisplayMode:
            widget.kind === 'map'
                ? widget.mapDataType === MapDataType.Marker
                    ? 'screens'
                    : widget.mapDisplayMode ?? 'heatmap'
                : undefined,
        source: inferSummaryWidgetSource({ ...widget, groupBy }),
        barOrientation: widget.kind === 'bar' ? widget.barOrientation ?? 'vertical' : undefined,
        expandedBarOrientation:
            widget.kind === 'bar' ? widget.expandedBarOrientation ?? 'vertical' : undefined,
        cumulative: widget.kind === 'line' ? widget.cumulative ?? true : undefined,
        expandedDisplayMode:
            widget.kind === 'bar' ? widget.expandedDisplayMode ?? undefined : undefined,
    };
    next.displayMode = inferSummaryWidgetDisplayMode(next);
    return next;
};

export const getSummaryWidgetConfigKey = (campaignId: number, isDsp: boolean) =>
    `${campaignId}:${isDsp ? 'dsp' : 'standard'}`;

export const getDefaultSummaryWidgets = (isDsp: boolean, preset: SummaryPresetKey = 'default') =>
    (isDsp ? SUMMARY_WIDGET_PRESETS[preset].dsp : SUMMARY_WIDGET_PRESETS[preset].standard).map(
        widget =>
            normalizeSummaryWidgetConfig({
                ...widget,
                groupBy: [...widget.groupBy],
            }),
    );

export const createEmptySummaryWidget = (): SummaryWidgetConfig => ({
    id: `summary_widget_${Date.now()}`,
    metric: 'impacts',
    kind: 'bar',
    groupBy: ['display_owner'],
    source: 'summary',
    mapDataType: undefined,
    mapDisplayMode: undefined,
});
